import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { privateRequest } from "services/privateRequest";
import toast from "react-hot-toast";

const BASE_URL = "/api/release";

// ✅ 1. Get all approvers for a release
export const getReleaseApprovers = createAsyncThunk(
  "releaseApprovers/getAll",
  async ({ releaseId, page = 1, size = 10 }, { rejectWithValue }) => {
    try {
      const res = await privateRequest.get(
        `${BASE_URL}/${releaseId}/approvers?page=${page}&size=${size}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ 2. Add new approver
export const addReleaseApprover = createAsyncThunk(
  "releaseApprovers/add",
  async ({ releaseId, userId }, { dispatch, rejectWithValue }) => {
    try {
      const payload = { userId, releaseId };
      const res = await privateRequest.post(`${BASE_URL}/approvers`, payload);
      toast.success("Approver added");
      // Don't refetch here - let the component handle it
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add approver");
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ 3. Update approver details (status, description) - UPDATED
export const updateReleaseApprover = createAsyncThunk(
  "releaseApprovers/update",
  async ({ releaseId, releaseApproveStatus, description = "", userId }, { dispatch, rejectWithValue }) => {
    try {
      const payload = {
        releaseId: parseInt(releaseId),
        releaseApproveStatus,
        description,
        userId: parseInt(userId)
      };
      
      const res = await privateRequest.put(
        `${BASE_URL}/${releaseId}/approvers`,
        payload
      );
      
      toast.success(`Approver ${releaseApproveStatus.toLowerCase()} successfully`);
      dispatch(getReleaseApprovers({ releaseId }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update approver");
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ 4. Delete approver
export const deleteReleaseApprover = createAsyncThunk(
  "releaseApprovers/delete",
  async ({ releaseId, approverId }, { rejectWithValue }) => {
    try {
      const res = await privateRequest.delete(
        `${BASE_URL}/${releaseId}/approvers/${approverId}`
      );
      toast.success("Approver removed");
      return { ...res.data, deletedApproverId: approverId };
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete approver");
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const releaseApproverSlice = createSlice({
  name: "releaseApprovers",
  initialState: {
    list: [],
    totalPages: 0,
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReleaseApprovers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReleaseApprovers.fulfilled, (state, action) => {
        state.loading = false;
        
        // Fix: Match the actual API response structure
        if (action.payload?.data?.data) {
          // Transform the API data to match our component needs
          state.list = action.payload.data.data.map(approver => ({
            id: approver.approverUser?.userId, // Using userId as id
            userId: approver.approverUser?.userId,
            userName: approver.approverUser?.firstName || approver.approverUser?.email,
            email: approver.approverUser?.email,
            profileColour: approver.approverUser?.profileColour,
            status: approver.releaseApproveStatus,
            description: approver.description || "",
            releaseApproveStatus: approver.releaseApproveStatus, 
          }));
        } else {
          state.list = [];
        }
        
        state.totalPages = action.payload?.data?.count?.total || 0;
      })
      .addCase(getReleaseApprovers.rejected, (state) => {
        state.loading = false;
        state.list = [];
      })
      
      // Optimistically remove approver when delete is successful
      .addCase(deleteReleaseApprover.fulfilled, (state, action) => {
        state.list = state.list.filter(
          approver => approver.id !== action.payload.deletedApproverId
        );
      });
  },
});

export default releaseApproverSlice.reducer;